import { memo, useCallback, useId, useRef, useState } from 'react'
import { useSearchParams } from 'react-router'

import { TabPanel, TabPanels, Tabs, chakra } from '@chakra-ui/react'
import type SwiperType from 'swiper'
// @ts-expect-error Test
import 'swiper/css'
// @ts-expect-error Test
import 'swiper/css/free-mode'
// @ts-expect-error Test
import 'swiper/css/navigation'
import { FreeMode, Navigation } from 'swiper/modules'
import { Swiper, SwiperSlide } from 'swiper/react'

import { extractOfferFromPages } from '@wwt/shared/utils'
import { useSearchResponseStore } from '@wwt/store/searchResponse'

import { useTabs } from '../../model/useTabs'
import { InfoTab } from './InfoTab'
import { RoomInfoTabsNavigationButton } from './RoomInfoTabsNavigationButton.tsx'

const ChakraSwiper = chakra(Swiper)

interface RoomInfoTabs {
	/**
	 * Callback which will be called when need open offer configuration
	 */
	onShowOfferConfiguration: (selectedFeature?: string) => void
}

export const RoomInfoTabs = memo((props: RoomInfoTabs) => {
	const swiperRef = useRef<SwiperType>(undefined)
	const prevButtonId = useId()
	const nextButtonId = useId()
	const [searchParams, setSearchParams] = useSearchParams()
	const [isSwiping, setIsSwiping] = useState(false)

	const offerId = useSearchResponseStore.use.selectedId()
	const pages = useSearchResponseStore.use.pages()

	const offer = extractOfferFromPages(pages, offerId)
	const tabs = useTabs(offer)

	const tabIndex = tabs.findIndex(tab => tab.label === searchParams.get('tab'))

	const handleTabsChange = useCallback(
		(tabIndex: number) => {
			setSearchParams(
				new URLSearchParams({
					...Object.fromEntries(searchParams),
					tab: tabs[tabIndex].label
				})
			)
		},
		[tabs]
	)

	return (
		<Tabs
			index={tabIndex >= 0 ? tabIndex : undefined}
			onChange={handleTabsChange}
			variant="unstyled"
			isLazy
		>
			<ChakraSwiper
				onBeforeInit={swiper => {
					swiperRef.current = swiper
				}}
				pos="relative"
				spaceBetween="2rem"
				slidesPerView="auto"
				freeMode={{ enabled: true }}
				navigation={{
					nextEl: `#${CSS.escape(nextButtonId)}`,
					prevEl: `#${CSS.escape(prevButtonId)}`
				}}
				onTouchStart={() => setIsSwiping(true)}
				onTouchEnd={() => setIsSwiping(false)}
				modules={[Navigation, FreeMode]}
				mb="1.875rem"
				px={{ base: '1.25rem', md: '0' }}
			>
				{tabs.map(tab => (
					<SwiperSlide
						style={{ width: 'fit-content' }}
						key={tab.label}
					>
						<InfoTab
							isDisabled={isSwiping}
							title={tab.label}
							hint={tab.hint}
						/>
					</SwiperSlide>
				))}

				<RoomInfoTabsNavigationButton
					id={prevButtonId}
					direction="left"
				/>

				<RoomInfoTabsNavigationButton
					id={nextButtonId}
					direction="right"
				/>
			</ChakraSwiper>

			<TabPanels>
				{tabs.map(tab => (
					<TabPanel
						p="0"
						key={tab.label}
					>
						<tab.component
							{...tab.props}
							offer={offer}
							{...props}
						/>
					</TabPanel>
				))}
			</TabPanels>
		</Tabs>
	)
})

RoomInfoTabs.displayName = 'RoomInfoTabs'
